import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  OnChanges,
  Output,
  Renderer2,
  SimpleChanges,
} from "@angular/core";
import currency from "currency.js";
import { TextInput } from "ionic-angular";

@Directive({
  host: {
    "(ionBlur)": "onBlur()",
    "(keydown)": "onKeyDown($event)",
    "(keyup)": "onKeyUp($event)",
  },
  inputs: [
    "showCurrencyPlaceHolder: show-ccy-placeholder",
    "externalValue: ngModel",
    "maxCharacterLength: ccy-max-length",
    "doAllowNegative: ccy-allow-negative",
  ],
  selector: "[app-ccy-typing]",
})
export class CurrencyTypingDirective implements AfterViewInit, OnChanges {
  private readonly host: HTMLElement & TextInput;
  private inputEl: HTMLInputElement;
  private decimalSeparator = ".";
  private thousandSeparator = ",";
  private numberOfDecimalDigits = 2;
  private maxValue = null;
  private maxCharacterLength = null;
  private doAllowNegative: string | boolean = false;
  private currentValue = null;
  private oldValue: string = null;
  private isAKeyPress: boolean = false;
  private toLocaleStringOptions = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    thousandSeparator: this.thousandSeparator,
    decimalSeparator: this.decimalSeparator,
  };
  @Output()
  ccyValueChange: EventEmitter<number> = new EventEmitter<number>();
  showCurrencyPlaceHolder: boolean | string = true;
  externalValue: number | string = null;
  private isCommandKeyPressed = false;

  constructor(private el: ElementRef, private _renderer: Renderer2) {
    this.host = el.nativeElement;
  }

  private get nonNumericCharacters() {
    return [this.decimalSeparator, this.thousandSeparator];
  }

  private isNumericCharacter(char: string): boolean {
    return this.nonNumericCharacters.indexOf(char) < 0;
  }

  public static isContentChangingKeyCode(keyCode: number) {
    // 48 - 57: 0 - 9
    // 96 - 105: numpad 0 - 9
    // 8: backspace
    // 46: delete key
    return (
      (keyCode >= 48 && keyCode <= 57) ||
      (keyCode >= 96 && keyCode <= 105) ||
      keyCode === 8 ||
      keyCode === 46 ||
      keyCode === 189 ||
      keyCode === 109
    );
  }

  private moveDecimalPoint(value: string): string {
    let splittedContent = value.toString().split("");
    if (value.length < 3) {
      for (let i = 0; i < 3 - value.length; i++) {
        splittedContent.unshift("0");
      }
    }

    if (value.length >= 3 && splittedContent.every((item) => item !== ".")) {
      splittedContent.push(".");
      splittedContent.push("0");
      splittedContent.push("0");
    }
    // Pushes the decimal point to before the last 2 digits
    splittedContent = splittedContent.filter(
      this.isNumericCharacter.bind(this)
    );

    const positionForDecimalPoint =
      splittedContent.length - this.numberOfDecimalDigits;
    splittedContent
      .splice(positionForDecimalPoint, 0, this.decimalSeparator)
      .join("");
    // Gets the new numeric value
    let newValue = parseFloat(splittedContent.join(""));
    if (this.maxValue) {
      if (newValue > this.maxValue) {
        newValue = this.currentValue;
      }
    }
    this.ccyValueChange.emit(newValue);
    this.currentValue = newValue;
    // Validate if the value is greater a max value (if provided)
    return newValue.toLocaleString("en-US", this.toLocaleStringOptions);
  }

  // onKeyUp({keyCode, target: {value}}) {
  //   // Checks for only doing this with numeric value from 0-9 only
  //   let doUpdateValue = true;
  //   // Strips any thing is not numeric character, decimal and thousand separator, negative sign from the text
  //   let updatedValue = value.replace(/[^0-9\.,-]/ig, '');
  //
  //   if (CurrencyTypingDirective.isContentChangingKeyCode(keyCode)) {
  //     let newValue = updatedValue;
  //     const moveNegativeSignProperly = (text) => {
  //       // Since we tap into the key up event, we have to take care of the case receiving the negative sign as
  //       // the only character
  //       if (text === '-') {
  //         return '-0.00';
  //       }
  //       // Also, we have to take care of the case receiving the negative sign as
  //       // the last character
  //       if (text.endsWith('-')) {
  //         // We remove the last negative sign
  //         text = text.substr(0, text.length - 1);
  //         // and move it to the first position
  //         if (!text.startsWith('-')) {
  //           text = '-' + text;
  //         }
  //       }
  //       return text;
  //     };
  //     if (this.isNegativeValid) {
  //       newValue = moveNegativeSignProperly(newValue);
  //     }
  //     if (this.maxCharLength) {
  //       // Strips all non-numeric characters from the text for determining the length
  //       const comparedValue = newValue.replace(/[-\.,]/ig, '');
  //       // We do not update value if the length of the new value is greater than the old one
  //       doUpdateValue = comparedValue.length <= this.maxCharLength;
  //     }
  //     updatedValue = doUpdateValue? newValue : value.substr(0, value.length - 1);
  //   }
  //   // We move the decimal point properly and update the input
  //   this.updateHtmlInputValue(this.moveDecimalPoint(updatedValue));
  // }

  private extractHtmlInputEl() {
    this.inputEl = (<HTMLElement>this.host).getElementsByTagName("input")[0];
  }

  private get doShowCurrencyPlaceHolder() {
    if (this.showCurrencyPlaceHolder === "false") {
      return false;
    }
    if (this.showCurrencyPlaceHolder === "") {
      return true;
    }
    return this.showCurrencyPlaceHolder;
  }

  private updateThePlaceHolder() {
    if (this.doShowCurrencyPlaceHolder) {
      this._renderer.setAttribute(this.inputEl, "placeholder", "0.00");
    }
  }

  private updateHtmlInputValue(value: string | number) {
    if (this.inputEl) {
      this._renderer.setProperty(this.inputEl, "value", value);
      // Dispatches event of the input element to update the value
      this.inputEl.dispatchEvent(new Event("input"));
    }
  }

  private onExternalValueChange(value: string) {
    this.updateHtmlInputValue(
      parseFloat(value).toLocaleString("en-US", this.toLocaleStringOptions)
    );
  }

  get isNegativeValid() {
    return (
      this.doAllowNegative !== null ||
      this.doAllowNegative === "" ||
      this.doAllowNegative === "1"
    );
  }

  get maxCharLength(): number {
    return parseInt(this.maxCharacterLength, 9);
  }

  ngAfterViewInit(): void {
    this.extractHtmlInputEl();
    this.updateThePlaceHolder();
    setTimeout(this.updateFormattedValue.bind(this), 150);
  }

  private updateFormattedValue(newValue: string = null, hasEventEmit = true) {
    if (this.inputEl) {
      let targetValue = newValue ? newValue.toString() : this.inputEl.value;
      let value = null;
      if (targetValue !== "") {
        const valueWithSeparators = currency(targetValue).format();
        const digitStringOnly = valueWithSeparators.replace(/[^0-9]/gi, "");
        if (digitStringOnly.length > this.maxCharLength) {
          targetValue = this.oldValue;
        }
        let parsedValue = targetValue.replace(/[^0-9.,-]/g, "");
        if (this.isNegativeNoStartMinusSign(parsedValue)) {
          parsedValue =
            "-" +
            parsedValue
              .split("")
              .filter((item) => item !== "-")
              .join("");
        }
        // If the value is invalid, remove all invalid character and try to parse again
        value = currency(parsedValue, {
          errorOnInvalid: true,
          decimal: ".",
          separator: ",",
        }).format();
      }
      this._renderer.setProperty(this.inputEl, "value", value);
      if (hasEventEmit) {
        this.inputEl.dispatchEvent(new Event("input"));
      }
    }
  }

  private hasInvalidLength() {
    if (this.maxCharLength) {
      return (
        this.inputEl.value.replace(/[^0-9.,-]/g, "").length >
        this.maxCharLength - this.numberOfDecimalDigits
      );
    }
    return false;
  }

  isNegativeNoStartMinusSign(value: string) {
    return (
      value.split("").some((item) => item === "-") && !value.startsWith("-")
    );
  }

  getLengthWithDecimalDigit(value: string) {
    const valueWithSeparators = currency(value).format();
    const digitStringOnly = valueWithSeparators.replace(/[^0-9]/gi, "");
    return digitStringOnly.length;
  }

  onBlur() {
    this.updateFormattedValue();
    this.isAKeyPress = false;
  }

  onKeyUp({ key, target: { value } }) {
    this.isAKeyPress = false;
    if (key === "Meta") {
      this.isCommandKeyPressed = false;
    }
    if (this.getLengthWithDecimalDigit(value) > this.maxCharLength) {
      this.updateFormattedValue();
      this._renderer.setProperty(this.inputEl, "value", this.oldValue);
    }
  }

  onKeyDown(event) {
    this.isAKeyPress = true;
    const {
      key,
      altKey,
      ctrlKey,
      target: { value },
    } = event;
    if (key === "Meta") {
      this.isCommandKeyPressed = true;
    }
    const onlyNumber = key.replace(/[0-9.,-]/gi, "") === "";
    const isControlKeyPressed = altKey || ctrlKey || this.isCommandKeyPressed;
    const nonCharacterKeyList = [
      "Delete",
      "Backspace",
      "ArrowRight",
      "ArrowLeft",
      "Tab",
      "Home",
      "End",
      "Control",
      "Meta",
      "Shift",
    ];
    const nonCharacterKeyPressed = nonCharacterKeyList.indexOf(key) > -1;
    let doStopTheKey = true;
    if (nonCharacterKeyPressed) {
      doStopTheKey = false;
    } else if (onlyNumber) {
      doStopTheKey = false;
    } else if (!onlyNumber && isControlKeyPressed) {
      doStopTheKey = false;
    }
    if (doStopTheKey) {
      event.preventDefault();
    } else {
      if (this.getLengthWithDecimalDigit(value) <= this.maxCharLength) {
        this.oldValue = value;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isAKeyPress) {
      setTimeout(
        this.updateFormattedValue.bind(this, [
          changes.externalValue.currentValue,
        ]),
        150
      );
    }
  }
}



// WEBPACK FOOTER //
// ./src/directive/currency-typing.directive.ts